import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@uigy/shared/services/language.service';
import {
  AssetCategory,
  ExprOp,
  GroupedAssetExpressionsParams,
  GroupedAssetsField,
  IcpPluginExpressionsParams,
  IcpPluginField,
  PluginSeverity,
} from '@uigy/generated/graphql';
import { MainKpiService } from '@uigy/modules/overview/kpis/kpi-main/services/main-kpi.service';
import { MainKpiConfig } from '@uigy/modules/overview/kpis/kpi.interfaces';
import { MainKpiConfigTypes } from '@uigy/modules/overview/kpis/kpi.types';
import { UiGridFilterModel, UiGridOptions } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { InventoryUiGridService } from '@uigy/modules/inventory/services/inventory-ui-grid.service';

@Injectable({
  providedIn: 'root'
})
export class MainKpiEMConfigService {
  private languageService = inject(LanguageService);
  private mainKpiService = inject(MainKpiService);
  private inventoryUiGridService = inject(InventoryUiGridService);

  private readonly texts = this.languageService.texts.overview.kpi_widgets;

  private readonly ControllersCategoryConfig: MainKpiConfig<GroupedAssetsField> = {
    filter: this.inventoryUiGridService.baseAssetFilter(AssetCategory.ControllersCategory),
    riskFilter: this.inventoryUiGridService.baseAssetFilter(AssetCategory.ControllersCategory, true),
    fetchData: (filters) => this.mainKpiService.fetchCombinedKpiCount<GroupedAssetExpressionsParams>(filters, this.mainKpiService.getGroupedAssetsRawGQL),
    title: this.texts.ot_controllers.title,
    subTitle: this.texts.sub_title,
    riskScoreTooltip: this.texts.risk_score_tooltip,
    isClickable: false
  };

  private readonly IotCategoryConfig: MainKpiConfig<GroupedAssetsField> = {
    filter: this.inventoryUiGridService.baseAssetFilter(AssetCategory.IotCategory),
    riskFilter: this.inventoryUiGridService.baseAssetFilter(AssetCategory.IotCategory, true),
    fetchData: filters => this.mainKpiService.fetchCombinedKpiCount<GroupedAssetExpressionsParams>(filters, this.mainKpiService.getGroupedAssetsRawGQL),
    title: this.texts.iot_assets.title,
    subTitle: this.texts.sub_title,
    riskScoreTooltip: this.texts.risk_score_tooltip,
    isClickable: false
  };

  private readonly NetworkAssetsCategoryConfig: MainKpiConfig<UiGridOptions<GroupedAssetsField>> = {
    filter: this.inventoryUiGridService.baseAssetFilter(AssetCategory.NetworkAssetsCategory),
    riskFilter: this.inventoryUiGridService.baseAssetFilter(AssetCategory.NetworkAssetsCategory, true),
    fetchData: filters => this.mainKpiService.fetchCombinedKpiCount<GroupedAssetExpressionsParams>(filters, this.mainKpiService.getGroupedAssetsRawGQL),
    title: this.texts.network_assets.title,
    subTitle: this.texts.sub_title,
    riskScoreTooltip: this.texts.risk_score_tooltip,
    isClickable: false
  };

  private readonly VulnerabilityConfig: MainKpiConfig<UiGridFilterModel<IcpPluginField>> = {
    filter: this.baseVulnerabilityFilter(),
    riskFilter: this.baseVulnerabilityFilter(true),
    fetchData: filters => this.mainKpiService.fetchCombinedKpiCount<IcpPluginExpressionsParams>(filters, this.mainKpiService.getVulnerabilitiesCount),
    title: this.texts.vulnerability.title,
    subTitle: this.texts.vulnerability.sub_title,
    riskScoreTooltip: this.texts.vulnerability.tooltip,
    isClickable: false,
  };

  constructor() {
  }

  public getKpiConfigs(): MainKpiConfigTypes[] {
    return [
      this.ControllersCategoryConfig,
      this.IotCategoryConfig,
      this.NetworkAssetsCategoryConfig,
      this.VulnerabilityConfig
    ];
  }

  private baseVulnerabilityFilter(riskCondition: boolean = false): UiGridFilterModel<IcpPluginField> {
    const pluginExpressionsParams: UiGridFilterModel<IcpPluginField> = {
      field: IcpPluginField.Severity,
      op: ExprOp.In,
      values: [PluginSeverity.Low, PluginSeverity.Info, PluginSeverity.Medium, PluginSeverity.High, PluginSeverity.Critical],
    };

    if (riskCondition) {
      return {
        ...pluginExpressionsParams,
        values: [PluginSeverity.High, PluginSeverity.Critical]
      };
    }

    return pluginExpressionsParams;
  }

}
